import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Table from 'src/components/VehicleTrackingDetails/Tables/Table';
import { ReactComponent as PlusIcon } from 'src/Images/icons/common/plus.svg';
import {
  getVehicleTrackingDetail,
  getPlants,
} from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as EditIcon } from 'src/Images/icons/common/EditIcon.svg';
import { ReactComponent as TareWeightIcon } from 'src/Images/icons/common/tareWeightIcon.svg';
import { ReactComponent as LoadWeightIcon } from 'src/Images/icons/common/loadWeightIcon.svg';
import { ReactComponent as VarianceLoadWeightIcon } from 'src/Images/icons/common/varienceLoadWeightIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import CustomCreatableSelect, {
  ManualEntryHelperText,
} from 'src/utils/CustomCreatableSelect';
import {
  getTransporterDropdownOptions,
  getVehicleDropdownOptions,
  updateVehicleTrackingDetails,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { Col, Row } from 'react-bootstrap';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';

function VehicleContentTable({ toggleAddVehicleContentDrawer }) {
  const dispatch = useDispatch();

  const { vehicleTrackingNumber, workflowStageId } = useParams();

  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state?.VehicleTrackingOutwardReducer,
  );
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;
  const { vehicleTrackingLineItemList, vehicleTrackingDetails } =
    vehicleTrackingDataReducer;
  const { plantsData } = vehicleTrackingDataReducer;
  const currentTransporterName =
    vehicleTrackingDetails?.transporter?.name ||
    vehicleTrackingDetails?.transporterExtraData;

  const { vehicleDropdownOptions, transporterDropdownOptions } =
    VehicleTrackingOutwardReducer;
  const [isBasicDetailsInEditMode, setIsBasicDetailsInEditMode] =
    useState(false);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [transporterSearchTerm, setTransporterSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    vehicleId: null,
    vehicleNumber: '',
    transporterId: null,
    transporterName: '',
    plantId: null,
  });
  const { vehicleTrackingAttachedLoads } = VehicleTrackingOutwardReducer;
  const { superAdmin, organisationAdmin } = authReducer;
  const { hasPermission } = usePermissions();
  const canCreateOutwardVehicleTracking = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE,
  );

  const isEditAllowed =
    (superAdmin || organisationAdmin || canCreateOutwardVehicleTracking) &&
    !vehicleTrackingDetails?.vehicleTrackingCompleted;

  const vehicleTrackingLoadData =
    vehicleTrackingAttachedLoads?.vehicleTrackingLoadData;

  const areAllLoadsCompleted =
    vehicleTrackingLoadData?.length > 0 &&
    vehicleTrackingLoadData.every((load) => load.completed);
  const showWeightAnalysis =
    vehicleTrackingDetails?.direction?.id === 2 && areAllLoadsCompleted;

  const canAddVehicleContent =
    vehicleTrackingDetails?.requestedStage?.allow_vehicle_content_additon &&
    vehicleTrackingDetails?.direction?.id === 1 &&
    !vehicleTrackingDetails?.requestedStage?.completed &&
    vehicleTrackingDetails?.requestedStage?.entry_log_exist;

  const vehicleContentTotalWeight =
    vehicleTrackingDetails?.direction?.id === 1
      ? vehicleTrackingDetails?.vehicleTrackingContentTotalWeight
      : vehicleTrackingDetails?.plannedGrossWeight;

  useEffect(() => {
    dispatch(
      getVehicleTrackingDetail(
        vehicleTrackingNumber,
        organisationId,
        workflowStageId,
      ),
    );
  }, [dispatch, vehicleTrackingNumber, organisationId, workflowStageId]);

  const hasBasicDetailsUpdatePermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.UPDATE_BASIC_DETAILS,
  );
  const hasOutwardUserPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.VIEW,
  );
  const canEditVehicleNumber =
    hasBasicDetailsUpdatePermission &&
    !vehicleTrackingDetails?.vehicleTrackingCompleted;
  const canEditVehicleTransporter =
    vehicleTrackingDetails?.direction?.id === 2 &&
    hasOutwardUserPermission &&
    canEditVehicleNumber;

  useEffect(() => {
    if (vehicleTrackingDetails?.direction?.id === 2 && organisationId) {
      dispatch(getPlants(organisationId));
    }
  }, [dispatch, organisationId, vehicleTrackingDetails?.direction?.id]);

  useEffect(() => {
    if (!vehicleTrackingDetails) return;
    setFormData({
      vehicleId: vehicleTrackingDetails.vehicle?.id ?? null,
      vehicleNumber: vehicleTrackingDetails.vehicleNumber || '',
      transporterId: vehicleTrackingDetails.transporter?.id ?? null,
      transporterName:
        vehicleTrackingDetails.transporter?.name ||
        vehicleTrackingDetails.transporterExtraData ||
        '',
      plantId: vehicleTrackingDetails.plant?.id ?? null,
    });
  }, [vehicleTrackingDetails]);

  const vehicleOptions = (vehicleDropdownOptions ?? []).map((vehicle) => ({
    value: vehicle.id,
    label: vehicle.vehicle_number,
  }));
  const transporterOptions = (transporterDropdownOptions ?? []).map(
    (transporter) => ({
      value: transporter.id,
      label: transporter.name,
    }),
  );
  const plantOptions = (plantsData || []).map((plant) => ({
    value: plant.id,
    label: plant.name,
  }));
  const selectedPlantOption =
    plantOptions.find((option) => option.value === formData.plantId) || '';

  useEffect(() => {
    if (isBasicDetailsInEditMode) return;
    setVehicleSearchTerm('');
    setTransporterSearchTerm('');
  }, [isBasicDetailsInEditMode]);

  const handleVehicleMenuOpen = () => {
    dispatch(
      getVehicleDropdownOptions(
        organisationId,
        vehicleSearchTerm || null,
        null,
      ),
    );
  };

  const handleTransporterMenuOpen = () => {
    dispatch(
      getTransporterDropdownOptions(
        organisationId,
        transporterSearchTerm || null,
        null,
      ),
    );
  };

  const isManualVehicleEntry = formData.vehicleNumber && !formData.vehicleId;
  const isManualTransporterEntry =
    formData.transporterName && !formData.transporterId;

  const handleSave = (closeEditMode) => {
    const payload = {
      tenant_organisation_id: organisationId,
      vehicle_tracking_number: vehicleTrackingNumber,
      vehicle_id: formData.vehicleId || null,
      vehicle_number: formData.vehicleNumber || null,
    };

    if (canEditVehicleTransporter) {
      payload.transporter_id = formData.transporterId || null;
      payload.transporter_extra_data = formData.transporterName || null;
      payload.plant_id = formData.plantId || null;
    }

    dispatch(updateVehicleTrackingDetails(payload)).then(() => {
      closeEditMode(false);
      dispatch(
        getVehicleTrackingDetail(
          vehicleTrackingNumber,
          organisationId,
          workflowStageId,
        ),
      );
    });
  };

  const headers = [
    {
      label: 'PO Number',
      value: 'purchase_order_number',
    },
    { label: 'Invoice Number', value: 'invoice_number' },
    { label: 'E-Way Bill Number', value: 'eway_bill_number' },
    {
      label: 'Material Code',
      value: 'material_code',
    },
    {
      label: 'Material Description',
      value: 'material_description',
    },
    {
      label: 'PO Item Quantity',
      value: 'purchase_order_line_item_quantity',
    },
    {
      label: 'Pack Type',
      value: 'pack_type',
    },
    {
      label: 'Pack Quantity',
      value: 'pack_quantity',
    },
    {
      label: 'Receiving Quantity',
      value: 'receiving_quantity',
    },
    {
      label: 'Tare Weight',
      value: 'Pack_weight',
    },
  ];

  const renderTableContent = () => (
    <tbody>
      {vehicleTrackingLineItemList?.length > 0 ? (
        vehicleTrackingLineItemList.map((lineItem) => (
          <tr key={lineItem.id}>
            {headers.map((header) => {
              let content;
              // Special handling for certain fields
              if (header.value === 'purchase_order_line_item_quantity') {
                content = `${lineItem[header.value]} ${lineItem?.unit_of_measurement}`;
              } else if (header.value === 'Pack_weight') {
                content = lineItem.pack_weight;
              } else {
                content = lineItem[header.value];
              }

              return (
                <td
                  className="table-data"
                  key={`${lineItem.id}-${header.value}`}
                >
                  <div className="div-flex-row">
                    <span className="text-sm">{content}</span>
                  </div>
                </td>
              );
            })}
          </tr>
        ))
      ) : (
        <tr>
          <td
            colSpan={headers?.length}
            className="no-data-found-message border-bottom"
          >
            <span className="text-sm">No Vehicle Data Found</span>
          </td>
        </tr>
      )}
    </tbody>
  );
  return vehicleTrackingLineItemList?.length > 0 ? (
    <>
      {renderbasicDetailsCards()}
      <div className="div-flex-row div-space-between section-title div-align-center padding-left-right">
        <div className="div-flex-row align-items-center cg-10">
          <div className="text-md text-medium-bold">Vehicle Contents</div>
          {isEditAllowed && (
            <div
              role="button"
              onClick={() => toggleAddVehicleContentDrawer()}
              tabIndex={0}
              aria-label="Add Vehicle Content"
              className="text-underline-blue text-sm"
            >
              Edit Detail
            </div>
          )}
        </div>
        <span className="text-sm text-medium-bold">
          Total Content: {vehicleTrackingLineItemList?.length || 0}
        </span>
      </div>
      <div
        className={`table-fixed-container theme-horizontal-scrollable-container-${themeColor}`}
      >
        <Table
          headers={headers}
          minCellWidth={150}
          tableContent={renderTableContent()}
        />
      </div>
    </>
  ) : (
    <>
      {renderbasicDetailsCards()}
      {canAddVehicleContent && (
        <div className="vehicle-data-add-button-container div-flex-center div-align-center rg-10">
          <div className="div-flex-column div-align-center ">
            <span className="text-sm text-medium-bold">
              Vehicle Content not added yet!
            </span>
            <span className="text-sm">
              Start by adding contents this vehicle is carrying.
            </span>
          </div>
          <div
            className={`vehicle-data-add-btn theme-text-${themeColor} theme-border-color-${themeColor} div-flex-row align-items-center cg-10`}
            role="button"
            onClick={() => toggleAddVehicleContentDrawer()}
            tabIndex={0}
          >
            <PlusIcon color={getColorCode(themeColor)} />
            <span className="text-sm text-medium-bold">
              Add Vehicle Content
            </span>
          </div>
        </div>
      )}
    </>
  );

  function renderbasicDetailsCards() {
    function renderOutwardWeightAnalysis() {
      const actualTareWeight = Number(vehicleTrackingDetails?.tareWeight || 0);
      const actualGrossWeight = Number(
        vehicleTrackingDetails?.grossWeight || 0,
      );
      const plannedLoadWeight = Number(
        vehicleTrackingDetails?.plannedLoadGrossWeight || 0,
      );
      const plannedGrossWeight = Number(
        vehicleTrackingDetails?.plannedGrossWeight || 0,
      );
      const actualLoadWeight = Math.max(
        Number(vehicleTrackingDetails?.netWeight || 0),
        0,
      );

      const weightAnalysisDiff =
        actualTareWeight +
        actualGrossWeight -
        (actualTareWeight + plannedGrossWeight);
      const isExcess = weightAnalysisDiff > 0;

      const grossDiff = actualGrossWeight - plannedGrossWeight;
      const loadDiff = actualLoadWeight - plannedLoadWeight;

      const weightAnalysisTotalBar = isExcess
        ? actualTareWeight + actualGrossWeight
        : plannedGrossWeight;

      const getPosition = (value, total) => {
        if (!total) return 0;
        return (Math.max(value, 0) / total) * 100;
      };

      const formatWeight = (value) =>
        `${Number.isInteger(value) ? value : Number(value.toFixed(1))}kg`;

      const tarePosition = getPosition(
        actualTareWeight,
        weightAnalysisTotalBar,
      );
      const plannedGrossPosition = getPosition(
        plannedGrossWeight,
        weightAnalysisTotalBar,
      );
      const actualGrossValue = isExcess
        ? actualTareWeight + actualGrossWeight
        : actualGrossWeight;

      const actualGrossPosition = getPosition(
        actualGrossValue,
        weightAnalysisTotalBar,
      );

      return (
        <>
          <div className="div-flex-row div-space-between section-title div-align-center padding-left-right border-top">
            <div className="text-md text-medium-bold">Weight Analysis</div>
          </div>
          <div className="vehicle-weight-analysis-card mx-0">
            <div
              className="vehicle-weight-analysis-track"
              style={{
                '--planned-tare-left': `${tarePosition}%`,
                '--planned-gross-left': `${plannedGrossPosition}%`,
              }}
            >
              <div className="vehicle-weight-analysis-bar-row">
                <div
                  className="vehicle-weight-analysis-bar text-xs text-medium-bold tare"
                  style={{ width: `${tarePosition}%` }}
                >
                  <span>Tare Weight</span>
                </div>
                <div
                  className="vehicle-weight-analysis-bar text-xs text-medium-bold load"
                  style={{
                    width: `calc(${plannedGrossPosition - tarePosition}% - 6px)`,
                  }}
                >
                  <span>Load Weight</span>
                </div>
              </div>

              <div className="vehicle-weight-analysis-planned-marker planned-tare">
                <span className="text-sm text-bold">
                  {formatWeight(actualTareWeight)}
                </span>
              </div>

              <div
                className={`vehicle-weight-analysis-planned-marker planned-gross${
                  isExcess ? '' : ' shortage'
                }`}
              >
                <span className="text-sm text-bold">
                  {formatWeight(plannedGrossWeight)}
                </span>
              </div>

              <div
                className={`vehicle-weight-analysis-actual-marker ${
                  isExcess ? 'excess' : 'shortage'
                }`}
                style={{
                  left: `${Math.min(actualGrossPosition, plannedGrossPosition)}%`,
                  width: `${Math.abs(actualGrossPosition - plannedGrossPosition)}%`,
                }}
              >
                <span className="text-sm text-bold text-danger">
                  {formatWeight(Math.abs(weightAnalysisDiff))}
                </span>
                <span
                  className="vehicle-weight-analysis-dashed-pill"
                  style={{ width: '100%' }}
                />
              </div>

              <TareWeightIcon className="icon-size-xl planned-tare-icon" />
              <LoadWeightIcon className="icon-size-xl planned-gross-icon" />
            </div>
          </div>
          <div className="div-flex-row div-space-between section-title div-align-center padding-left-right">
            <div className="text-md text-medium-bold">Variance Analysis</div>
          </div>
          <div className="vehicle-weight-analysis-card variance mx-0">
            {(() => {
              const scaleMax = Math.max(actualLoadWeight, plannedLoadWeight, 1);
              const plannedPos = getPosition(plannedLoadWeight, scaleMax);
              const actualPos = getPosition(actualLoadWeight, scaleMax);
              const isLoadExcess = loadDiff > 0;

              return (
                <div
                  className="vehicle-weight-analysis-track variance"
                  style={{
                    '--analysis-bar-width': `${plannedPos}%`,
                    '--analysis-planned-left': `${plannedPos}%`,
                  }}
                >
                  <div className="vehicle-weight-analysis-bar-row">
                    <div className="vehicle-weight-analysis-bar text-xs text-medium-bold">
                      <span>Load Weight</span>
                    </div>
                  </div>

                  <div
                    className={`vehicle-weight-analysis-planned-marker${
                      isLoadExcess ? '' : ' shortage'
                    }`}
                  >
                    <span className="text-sm text-bold">
                      {formatWeight(plannedLoadWeight)}
                    </span>
                  </div>

                  <div
                    className={`vehicle-weight-analysis-actual-marker ${
                      isLoadExcess ? 'excess' : 'shortage'
                    }`}
                    style={{
                      left: `${Math.min(actualPos, plannedPos)}%`,
                      width: `${Math.abs(actualPos - plannedPos)}%`,
                    }}
                  >
                    <span className="text-sm text-bold text-danger">
                      {formatWeight(Math.abs(loadDiff))}
                    </span>
                    <span
                      className="vehicle-weight-analysis-dashed-pill"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="vehicle-weight-analysis-row-icon">
                    <VarianceLoadWeightIcon className="icon-size-xl" />
                  </div>
                </div>
              );
            })()}

            {(() => {
              const scaleMax = Math.max(
                actualGrossWeight,
                plannedGrossWeight,
                1,
              );
              const plannedPos = getPosition(plannedGrossWeight, scaleMax);
              const actualPos = getPosition(actualGrossWeight, scaleMax);
              const isGrossExcess = grossDiff > 0;

              return (
                <div
                  className="vehicle-weight-analysis-track variance"
                  style={{
                    '--analysis-bar-width': `${plannedPos}%`,
                    '--analysis-planned-left': `${plannedPos}%`,
                  }}
                >
                  <div className="vehicle-weight-analysis-bar-row">
                    <div className="vehicle-weight-analysis-bar text-xs text-medium-bold">
                      <span>Gross Weight</span>
                    </div>
                  </div>

                  <div
                    className={`vehicle-weight-analysis-planned-marker${
                      isGrossExcess ? '' : ' shortage'
                    }`}
                  >
                    <span className="text-sm text-bold">
                      {formatWeight(plannedGrossWeight)}
                    </span>
                  </div>

                  <div
                    className={`vehicle-weight-analysis-actual-marker ${
                      isGrossExcess ? 'excess' : 'shortage'
                    }`}
                    style={{
                      left: `${Math.min(actualPos, plannedPos)}%`,
                      width: `${Math.abs(actualPos - plannedPos)}%`,
                    }}
                  >
                    <span className="text-sm text-bold text-danger">
                      {formatWeight(Math.abs(grossDiff))}
                    </span>
                    <span
                      className="vehicle-weight-analysis-dashed-pill"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="vehicle-weight-analysis-row-icon">
                    <LoadWeightIcon className="icon-size-xl" />
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="div-flex-row div-space-between padding-left-right padding-bottom-top border-bottom div-align-center">
          <div className="text-md text-medium-bold">Basic Details</div>
          {(canEditVehicleNumber || canEditVehicleTransporter) &&
            (isBasicDetailsInEditMode ? (
              <div className="div-flex-row div-align-center cg-10">
                <button
                  type="button"
                  className="common-btn-outlined"
                  onClick={() => setIsBasicDetailsInEditMode(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`common-btn-filled theme-background-${themeColor}`}
                  onClick={() => handleSave(setIsBasicDetailsInEditMode)}
                >
                  Submit
                </button>
              </div>
            ) : (
              <EditIcon
                className="icon-size-xl cursor-pointer"
                color="#9F9F9F"
                onClick={() => setIsBasicDetailsInEditMode(true)}
              />
            ))}
        </div>
        <Row className="mx-0">
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Vehicle Number</span>
              {isBasicDetailsInEditMode ? (
                <>
                  <CustomCreatableSelect
                    name="vehicleNumber"
                    className="multi-select-input-section margin-top-5"
                    classNamePrefix="react-select"
                    placeholder="Search or enter Vehicle Number"
                    options={vehicleOptions}
                    value={formData.vehicleNumber}
                    onChange={(vehicleNumber, vehicleId) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicleId,
                        vehicleNumber: vehicleId
                          ? vehicleNumber
                          : (vehicleNumber ?? '').toUpperCase(),
                      }))
                    }
                    onSearch={(searchValue) =>
                      setVehicleSearchTerm(searchValue.toUpperCase())
                    }
                    onMenuOpen={handleVehicleMenuOpen}
                    themeColor={themeColor}
                  />
                  {isManualVehicleEntry && (
                    <ManualEntryHelperText themeColor={themeColor} />
                  )}
                </>
              ) : vehicleTrackingDetails?.vehicleNumber ? (
                <span className="text-sm text-medium-bold">
                  {vehicleTrackingDetails.vehicleNumber}
                </span>
              ) : canEditVehicleNumber ? (
                <button
                  type="button"
                  className="text-underline-blue text-sm align-self-start"
                  onClick={() => setIsBasicDetailsInEditMode(true)}
                >
                  Create Vehicle
                </button>
              ) : (
                <span className="text-sm text-medium-bold">-</span>
              )}
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Transporter Name</span>
              {canEditVehicleTransporter && isBasicDetailsInEditMode ? (
                <>
                  <CustomCreatableSelect
                    name="transporterName"
                    className="multi-select-input-section margin-top-5"
                    classNamePrefix="react-select"
                    placeholder="Search or enter Transporter Name"
                    options={transporterOptions}
                    value={formData.transporterName}
                    onChange={(transporterName, transporterId) =>
                      setFormData((prev) => ({
                        ...prev,
                        transporterId,
                        transporterName: transporterName ?? '',
                      }))
                    }
                    onSearch={setTransporterSearchTerm}
                    onMenuOpen={handleTransporterMenuOpen}
                    themeColor={themeColor}
                  />
                  {isManualTransporterEntry && (
                    <ManualEntryHelperText themeColor={themeColor} />
                  )}
                </>
              ) : currentTransporterName ? (
                <span className="text-sm text-medium-bold">
                  {currentTransporterName}
                </span>
              ) : canEditVehicleTransporter ? (
                <button
                  type="button"
                  className="text-underline-blue text-sm align-self-start"
                  onClick={() => setIsBasicDetailsInEditMode(true)}
                >
                  Create Transporter
                </button>
              ) : (
                <span className="text-sm text-medium-bold">-</span>
              )}
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Workflow Name</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails.workflowName || '-'}
              </span>
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Plant</span>
              {canEditVehicleTransporter && isBasicDetailsInEditMode ? (
                <CustomMultiSelect
                  isMulti={false}
                  className="multi-select-input-section margin-top-5"
                  options={plantOptions}
                  value={selectedPlantOption}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      plantId: option?.value || null,
                    }))
                  }
                  classNamePrefix="react-select"
                  placeholder="Select Plant"
                />
              ) : (
                <span className="text-sm text-medium-bold">
                  {vehicleTrackingDetails?.plantNames?.join(', ') || '-'}
                </span>
              )}
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Direction</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.direction?.id === 1
                  ? 'Inward'
                  : 'Outward'}
              </span>
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Gross Weight</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.grossWeight
                  ? `${vehicleTrackingDetails?.grossWeight} KG`
                  : '-'}
              </span>
            </div>
          </Col>

          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Tare Weight</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.tareWeight
                  ? `${vehicleTrackingDetails?.tareWeight} KG`
                  : '-'}
              </span>
            </div>
          </Col>

          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Net Weight</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.netWeight
                  ? `${vehicleTrackingDetails?.netWeight} KG`
                  : '-'}
              </span>
            </div>
          </Col>

          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Vehicle Content Total Weight</span>
              <span className="text-sm text-medium-bold">
                {vehicleContentTotalWeight != null
                  ? `${vehicleContentTotalWeight} KG`
                  : '-'}
              </span>
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Reason</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.reason?.name || '-'}
              </span>
            </div>
          </Col>
          <Col
            xxl={3}
            xl={3}
            lg={3}
            md={6}
            xs={6}
            className="form-input-container"
          >
            <div className="div-flex-column">
              <span className="text-sm">Reason Extra Data</span>
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.reasonExtraData || '-'}
              </span>
            </div>
          </Col>
        </Row>
        {showWeightAnalysis && renderOutwardWeightAnalysis()}
      </>
    );
  }
}

export default VehicleContentTable;
